import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { ArrowLeft, Wifi, Check, Zap } from 'lucide-react';
import { smePlans } from '../data/mock';

const SMEPlansPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 via-slate-900 to-gray-900">
      {/* Hero Section */}
      <div className="relative overflow-hidden py-20 px-4">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-20">
          <div className="absolute inset-0 bg-gradient-to-r from-orange-500/20 to-amber-500/20"></div>
        </div>

        <div className="container mx-auto relative z-10">
          {/* Back Button */}
          <Button
            onClick={() => navigate('/')}
            variant="outline"
            className="mb-8 border-white/30 text-white hover:bg-white/10"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Button>

          {/* Title Section */}
          <div className="text-center max-w-4xl mx-auto mb-16">
            <div className="flex justify-center mb-6">
              <Wifi className="w-16 h-16 text-orange-400" />
            </div>
            <h1 className="text-5xl sm:text-6xl font-bold text-white mb-4">
              Unlimited <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-amber-400">SME Internet Plans</span>
            </h1>
            <p className="text-2xl text-gray-300 mb-2">
              Tailored for Businesses
            </p>
            <div className="inline-flex items-center gap-2 bg-orange-500/20 border border-orange-500/30 rounded-full px-6 py-3 mt-4">
              <Zap className="w-5 h-5 text-orange-400" />
              <span className="text-lg text-orange-300 font-semibold">All Plans Valid for 30 Days</span>
            </div>
          </div>

          {/* Plans Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto">
            {smePlans.map((plan, index) => (
              <Card
                key={plan.id}
                className={`relative border-2 transition-all duration-300 hover:scale-105 hover:shadow-2xl ${
                  plan.popular
                    ? 'border-orange-500 shadow-xl shadow-orange-500/30 bg-gradient-to-b from-gray-800 to-gray-900'
                    : 'border-gray-700 bg-gradient-to-b from-gray-800 to-gray-900 hover:border-orange-500/50'
                }`}
                style={{ animationDelay: `${index * 50}ms` }}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                    <div className="bg-gradient-to-r from-orange-500 to-amber-500 text-white px-4 py-1 rounded-full text-sm font-bold shadow-lg">
                      Best Value
                    </div>
                  </div>
                )}

                <CardContent className="pt-8 pb-6">
                  {/* Speed Badge */}
                  <div className="text-center mb-6">
                    <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-orange-500 to-amber-500 rounded-full mb-4 shadow-lg shadow-orange-500/50">
                      <Wifi className="w-10 h-10 text-white" />
                    </div>
                    <div className="text-4xl font-bold text-white mb-2">{plan.speed}</div>
                    <div className="text-sm text-gray-400">{plan.name}</div>
                  </div>

                  {/* Price */}
                  <div className="text-center mb-6 pb-6 border-b border-gray-700">
                    <div className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-amber-400">
                      {plan.price}
                    </div>
                    <div className="text-sm text-gray-400 mt-1">For {plan.validity}</div>
                  </div>

                  {/* Features */}
                  <ul className="space-y-3 mb-6">
                    {plan.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-gray-300">
                        <Check className="w-5 h-5 text-orange-400 flex-shrink-0" />
                        <span className="text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  {/* CTA Button */}
                  <Button
                    onClick={() => {
                      document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
                      navigate('/', { state: { scrollTo: 'contact' } });
                    }}
                    className={`w-full py-6 text-base font-semibold rounded-lg transition-all duration-300 ${
                      plan.popular
                        ? 'bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white shadow-lg shadow-orange-500/30'
                        : 'bg-gray-700 hover:bg-gray-600 text-white'
                    }`}
                  >
                    Choose Plan
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Bottom CTA */}
          <div className="text-center mt-16 max-w-3xl mx-auto">
            <div className="bg-gradient-to-r from-gray-800 to-gray-900 border-2 border-orange-500/30 rounded-2xl p-8">
              <h3 className="text-2xl font-bold text-white mb-3">
                High-Speed Internet for Small Medium Enterprises
              </h3>
              <p className="text-gray-400 mb-6">
                Get reliable, high-speed connectivity tailored for your business needs. All plans include unlimited data and 24/7 technical support.
              </p>
              <Button
                onClick={() => {
                  navigate('/', { state: { scrollTo: 'contact' } });
                }}
                size="lg"
                className="bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white px-8 py-6 text-lg font-semibold rounded-lg shadow-lg shadow-orange-500/30"
              >
                Get Started Now
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SMEPlansPage;
